import { Link } from 'react-router-dom';
import { Aperture, ArrowRight, Camera, Check, Clock, Copy, Lightbulb, MapPin, Palette, Share2, Shirt, Sparkles, Users } from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Reveal } from '@/components/Reveal';
import { MagneticButton } from '@/components/MagneticButton';
import { keyedList } from '@/lib/keys';
import { BLUEPRINT_PAGE } from '@/constants/testIds';
import { Card, CardKicker, SectionHeader, priorityBadge } from './primitives';

const CheckList = ({ items, tone = 'muted' }) => (
  <ul className="space-y-2.5">
    {keyedList(items).map(({ key, item }) => (
      <li key={key} className={`flex items-start gap-2 text-sm leading-relaxed ${tone === 'muted' ? 'text-[hsl(var(--lxra-muted))]' : 'text-[hsl(var(--lxra-fg))]'}`}>
        <Check className="h-4 w-4 text-[hsl(var(--lxra-gold))] flex-shrink-0 mt-0.5" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

/** Cream strip with the four request facts. */
export const SummaryStrip = ({ request: req }) => {
  const facts = [
    { label: 'Occasion', value: req.occasion },
    { label: 'Ideal Light', value: req.time_of_day },
    { label: 'In Frame', value: `${req.people_count || 1} ${req.people_count === 1 ? 'person' : 'people'}` },
    { label: 'Date', value: req.session_date || 'Flexible' },
  ];
  return (
    <section className="bg-[hsl(var(--lxra-cream))] text-[#0A0A0A]" data-testid={BLUEPRINT_PAGE.section('summary')}>
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
        {facts.map((s) => (
          <div key={s.label}>
            <p className="text-[10px] uppercase tracking-[0.18em] text-black/50">{s.label}</p>
            <p className="mt-1 text-sm font-medium capitalize truncate">{s.value || '—'}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

export const DirectionSection = ({ content: c }) => (
  <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-16 lg:py-20" data-testid={BLUEPRINT_PAGE.section('direction')}>
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16">
      <Reveal className="lg:col-span-3">
        <SectionHeader icon={Sparkles} kicker="Creative Direction" title="THE STORY WE'LL TELL." />
        <div className="space-y-4">
          {keyedList((c.creative_direction || '').split(/\n\n+/)).map(({ key, item: p }) => (
            <p key={key} className="text-sm sm:text-base text-[hsl(var(--lxra-muted))] leading-relaxed">{p}</p>
          ))}
        </div>
      </Reveal>
      <Reveal delay={0.1} className="lg:col-span-2">
        <Card>
          <CardKicker icon={Palette}>Palette</CardKicker>
          <ul className="mt-4 space-y-2.5">
            {(c.color_palette || []).map((col) => (
              <li key={col} className="flex items-center gap-3 text-sm text-[hsl(var(--lxra-fg))]">
                <span className="h-2 w-2 rounded-full bg-[hsl(var(--lxra-gold))]" />
                <span className="capitalize">{col}</span>
              </li>
            ))}
          </ul>
        </Card>
        {c.composition_notes?.length > 0 && (
          <Card className="mt-5">
            <CardKicker icon={Aperture}>Composition</CardKicker>
            <div className="mt-4"><CheckList items={c.composition_notes} /></div>
          </Card>
        )}
      </Reveal>
    </div>
  </section>
);

export const LocationsSection = ({ content: c }) => {
  const lighting = [
    ['Best window', c.lighting?.recommended_time],
    ['Natural light', c.lighting?.natural_light],
    ['Added light', c.lighting?.artificial_light],
    ['If weather turns', c.lighting?.weather_backup],
  ].filter(([, v]) => v);
  return (
    <section className="border-t border-[hsl(var(--lxra-border))]" data-testid={BLUEPRINT_PAGE.section('locations')}>
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-16 lg:py-20">
        <Reveal><SectionHeader icon={MapPin} kicker="Locations & Light" title="WHERE AND WHEN." /></Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {keyedList(c.locations, (l) => l.name).map(({ key, item: loc }, i) => (
              <Reveal key={key} delay={i * 0.05}>
                <Card className="h-full">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--lxra-muted))]">{loc.area}</p>
                  <h3 className="font-display text-xl tracking-[0.04em] text-[hsl(var(--lxra-fg))] mt-1">{loc.name}</h3>
                  <p className="mt-3 text-sm text-[hsl(var(--lxra-muted))] leading-relaxed">{loc.why_it_works}</p>
                  {loc.best_time && (
                    <p className="mt-3 flex items-center gap-1.5 text-xs text-[hsl(var(--lxra-gold))]"><Clock className="h-3.5 w-3.5" /> {loc.best_time}</p>
                  )}
                </Card>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <Card className="h-full bg-[#141416]">
              <CardKicker icon={Lightbulb}>Lighting Plan</CardKicker>
              <dl className="mt-4 space-y-4 text-sm">
                {lighting.map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--lxra-muted))]">{k}</dt>
                    <dd className="mt-1 text-[hsl(var(--lxra-fg))] leading-relaxed">{v}</dd>
                  </div>
                ))}
              </dl>
            </Card>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export const ShotListSection = ({ content: c }) => (
  <section className="border-t border-[hsl(var(--lxra-border))]" data-testid={BLUEPRINT_PAGE.section('shots')}>
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-16 lg:py-20">
      <Reveal><SectionHeader icon={Camera} kicker="Shot List" title="SCENE BY SCENE." /></Reveal>
      <Accordion type="multiple" defaultValue={(c.shot_list || []).slice(0, 1).map((_, i) => `scene-${i}`)} className="space-y-3">
        {keyedList(c.shot_list, (s) => s.scene).map(({ key, item: scene }, i) => (
          <AccordionItem key={key} value={`scene-${i}`} className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] px-6" data-testid={`blueprint-scene-${i}`}>
            <AccordionTrigger className="hover:no-underline py-5">
              <div className="flex items-center gap-4 text-left">
                <span className="font-display text-2xl text-[hsl(var(--lxra-gold))] w-8">{String(i + 1).padStart(2, '0')}</span>
                <div>
                  <h3 className="font-display text-xl tracking-[0.04em] text-[hsl(var(--lxra-fg))]">{scene.scene}</h3>
                  {scene.location_hint && <p className="text-xs text-[hsl(var(--lxra-muted))] mt-0.5">{scene.location_hint}</p>}
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-6">
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {keyedList(scene.shots, (s) => s.description || s.shot || s.title).map(({ key: shotKey, item: shot }) => (
                  <li key={shotKey} className="rounded-xl border border-[hsl(var(--lxra-border))] bg-[#0A0A0A] p-4">
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm font-medium text-[hsl(var(--lxra-fg))]">{shot.title}</p>
                      <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-[0.1em] ${priorityBadge(shot.priority)}`}>
                        {shot.priority === 'must' ? 'Must' : 'Nice'}
                      </span>
                    </div>
                    <p className="mt-1.5 text-xs text-[hsl(var(--lxra-muted))] leading-relaxed">{shot.description}</p>
                    <p className="mt-2 text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--lxra-gold))]/80">{shot.framing}</p>
                  </li>
                ))}
              </ul>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

export const PosesStyleSection = ({ content: c }) => (
  <section className="border-t border-[hsl(var(--lxra-border))]" data-testid={BLUEPRINT_PAGE.section('poses')}>
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-2 gap-12">
      <Reveal>
        <SectionHeader icon={Users} kicker="Direction" title="POSES & MOMENTS." />
        <ul className="space-y-4">
          {keyedList(c.poses, (p) => p.name).map(({ key, item: p }) => (
            <li key={key} className="flex gap-4">
              <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[hsl(var(--lxra-gold))] flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-[hsl(var(--lxra-fg))]">{p.name}</p>
                <p className="mt-1 text-sm text-[hsl(var(--lxra-muted))] leading-relaxed">{p.description}</p>
                {p.best_for && <p className="mt-1 text-[11px] uppercase tracking-[0.12em] text-[hsl(var(--lxra-gold))]/80">{p.best_for}</p>}
              </div>
            </li>
          ))}
        </ul>
      </Reveal>
      <Reveal delay={0.1}>
        <SectionHeader icon={Shirt} kicker="Styling" title="WHAT TO WEAR." />
        <Card><CheckList items={c.style_tips} /></Card>
      </Reveal>
    </div>
  </section>
);

export const TimelineDeliverablesSection = ({ content: c }) => (
  <section className="border-t border-[hsl(var(--lxra-border))]" data-testid={BLUEPRINT_PAGE.section('timeline')}>
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-2 gap-12">
      <Reveal>
        <SectionHeader icon={Clock} kicker="Timeline" title="HOW THE SESSION FLOWS." />
        <ol className="relative border-l border-[hsl(var(--lxra-border))] ml-2 space-y-6">
          {keyedList(c.timeline, (t) => `${t.time}-${t.activity}`).map(({ key, item: t }) => (
            <li key={key} className="pl-6 relative">
              <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full bg-[hsl(var(--lxra-gold))]" />
              <p className="text-[11px] uppercase tracking-[0.14em] text-[hsl(var(--lxra-gold))]">{t.time} · {t.duration_minutes} min</p>
              <p className="mt-1 text-sm text-[hsl(var(--lxra-fg))]">{t.activity}</p>
            </li>
          ))}
        </ol>
      </Reveal>
      <Reveal delay={0.1}>
        <SectionHeader icon={Share2} kicker="Deliverables" title="WHERE IT LIVES." />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {keyedList(c.deliverables, (d) => `${d.platform}-${d.format}`).map(({ key, item: d }) => (
            <Card key={key}>
              <p className="font-display text-lg tracking-[0.04em] text-[hsl(var(--lxra-fg))]">{d.platform}</p>
              <p className="mt-1 text-xs text-[hsl(var(--lxra-gold))] uppercase tracking-[0.12em]">{[d.format, d.aspect_ratio].filter(Boolean).join(' · ')}</p>
              {d.notes && <p className="mt-2 text-xs text-[hsl(var(--lxra-muted))] leading-relaxed">{d.notes}</p>}
            </Card>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

export const BlueprintCTA = ({ bookingUrl, onCopyLink }) => (
  <section className="border-t border-[hsl(var(--lxra-gold))]/30" data-testid={BLUEPRINT_PAGE.section('cta')}>
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
      <Reveal>
        <p className="text-xs tracking-[0.2em] uppercase text-[hsl(var(--lxra-gold))]">Love the direction?</p>
        <h2 className="font-display text-3xl sm:text-4xl mt-2 tracking-[0.03em] text-[hsl(var(--lxra-fg))]">LET&apos;S MAKE IT REAL.</h2>
        <p className="mt-3 max-w-md text-sm text-[hsl(var(--lxra-muted))] leading-relaxed">
          Your photographer will refine this blueprint with you before the shoot. Book your date, or reach out with questions.
        </p>
      </Reveal>
      <Reveal delay={0.1} className="flex flex-wrap gap-3">
        <MagneticButton>
          <Link to={bookingUrl} data-testid={`${BLUEPRINT_PAGE.bookNow}-footer`}>
            <button className="inline-flex items-center h-12 px-7 rounded-xl bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] transition-colors duration-200 text-xs uppercase tracking-[0.1em] font-semibold whitespace-nowrap">
              Book Now
              <ArrowRight className="ml-2 h-4 w-4" />
            </button>
          </Link>
        </MagneticButton>
        <Link to="/contact" data-testid={BLUEPRINT_PAGE.contact} className="inline-flex items-center h-12 px-6 rounded-xl border border-[hsl(var(--lxra-gold))]/50 text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] transition-colors duration-200 text-xs uppercase tracking-[0.1em] font-medium">
          Ask the Studio
        </Link>
        <button type="button" onClick={onCopyLink} data-testid={`${BLUEPRINT_PAGE.copyLink}-footer`} className="inline-flex items-center h-12 px-4 rounded-xl text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] transition-colors duration-200 text-xs uppercase tracking-[0.1em]" aria-label="Copy blueprint link">
          <Copy className="h-4 w-4" />
        </button>
      </Reveal>
    </div>
  </section>
);
